import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { HistoryContactFilter, HistoryContactSource } from '@history/core/cache/history-contact-source.service';
import { HistoryCsrCacheService } from '@history/core/cache/history-csr-cache.service';
import { HistoryService } from '@history/core/history.service';

import {
  capitalize,
  Civility,
  CngTableDefBuilder,
  CustomerFacade,
  initialBooking,
  joiner,
  MomentService,
  Option,
  rowOfAction,
  rowsOfAction,
  TranslateService,
  UntilDestroy,
  untilDestroyed,
  uppercase,
  UserDoc,
} from '@contact/upgrade';

enum TableAction {
  ADD = 'ADD',
  IMPORT = 'IMPORT',
  MERGE = 'MERGE',
  EDIT = 'EDIT',
  CONSULT = 'CONSULT',
  DELETE = 'DELETE',
}

@UntilDestroy()
@Component({
  selector: 'cng-history-contact-view',
  templateUrl: 'contact-view.component.html',
  styleUrls: ['./contact-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryContactViewComponent implements OnInit, OnDestroy, AfterViewInit {
  TableAction = TableAction;
  Filter = HistoryContactFilter;

  // table definition
  tableDef = this.tdb
    .from(this)
    .setPageOptions([10, 20, 50])
    .setPageSize(10)
    .setFilterExpanded(false)
    .setFilterCanExpand(true)
    .setFilterAutoFill(false)
    .paramStored('HistoryContactViewTable', this.historyContactSource)
    .build();

  csrs$ = this.historyCsrCacheService.getOnce();
  repository$ = this.customerFacade.repository$;
  repositories$ = this.historyService.getAllRepositories();

  private _csrs: UserDoc[] = [];
  private _repository: string | undefined = undefined;

  constructor(
    private readonly tdb: CngTableDefBuilder,
    private readonly historyContactSource: HistoryContactSource,
    private readonly momentService: MomentService,
    private readonly customerFacade: CustomerFacade,
    private readonly historyService: HistoryService,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService,
    private readonly historyCsrCacheService: HistoryCsrCacheService
  ) {}

  ngOnInit() {
    this.csrs$.pipe(untilDestroyed(this)).subscribe(csrs => (this._csrs = csrs));
    this.repository$.pipe(untilDestroyed(this)).subscribe(repository => (this._repository = repository));
  }

  /**
   * used to build cell content for contact name as contactFirstName + ' ' + contactLastName + ' ' + contactCompanyName
   * @param contactFirstName the contact first name
   * @param contactLastName the contact last name
   * @param contactCompanyName the company name
   */
  private buildCellTextContent(contactFirstName: string, contactLastName: string, contactCompanyName: string) {
    const firstname = capitalize(contactFirstName);
    const lastname = capitalize(contactLastName);
    const companyname = uppercase(contactCompanyName);
    return joiner([firstname, lastname, companyname], ' ');
  }

  /**
   * used to build customer name as contactFirstName + ' ' + contactLastName + ' - ' + contactCompanyName
   * @param contactFirstName the contact first name
   * @param contactLastName the contact last name
   * @param contactCompanyName the company name
   */
  private buildCustomerName(contactFirstName: string, contactLastName: string, contactCompanyName: string) {
    const firstname = capitalize(contactFirstName);
    const lastname = capitalize(contactLastName);
    const companyname = uppercase(contactCompanyName);
    const names = joiner([firstname, lastname], ' ');
    return joiner([names, companyname], ' - ');
  }

  ngAfterViewInit() {
    // columns definition
    this.tableDef.columnDefs = [
      {
        key: 'civility',
        header: 'commons:basic.civility',
        cell: ({ civility }) => (civility && civility !== Civility.NOTSET ? `commons:basic.civility_${civility.toLowerCase()}` : ''),
        sort: true,
      },
      {
        key: 'contactFirstName',
        header: 'commons:basic.contact_header',
        cell: ({ contactFirstName, contactLastName, contactCompanyName }) =>
          this.buildCellTextContent(contactFirstName, contactLastName, contactCompanyName),
        sort: true,
      },
      {
        key: 'addresses',
        header: 'commons:basic.contactUri',
        cell: ({ addresses }) =>
          addresses &&
          joiner(
            addresses.map(addr => addr.uri),
            ', '
          ),
        sort: false,
      },
      {
        key: 'contactId',
        header: 'commons:basic.contactId',
        sort: true,
      },
      {
        key: 'description',
        header: 'commons:basic.description',
        sort: false,
      },
      {
        key: 'creationDate',
        header: 'commons:basic.creationDate',
        cell: ({ creationDate }) => creationDate && this.momentService.readableDateTimeNoSep(creationDate),
        sort: true,
      },
    ];
    // actions behavior
    const actions$ = this.tableDef.actions$;
    // Add
    actions$.pipe(rowsOfAction(TableAction.ADD), untilDestroyed(this)).subscribe(() => this.customerFacade.createDialog());
    // Import
    actions$.pipe(rowsOfAction(TableAction.IMPORT), untilDestroyed(this)).subscribe(() =>
      // TODO replace by Angular import popin
      this.modalService.open({
        keyboard: false,
        backdrop: false,
        fullscreen: true,
        ctrlKeyboard: true,
        component: 'historyImportCsvView',
        modalId: 'histoImportCsv',
        title: '',
        resolve: {
          csrList: () => this._csrs,
          customerRepositoryName: () => this._repository,
        },
      })
    );
    // Merge
    actions$
      .pipe(rowsOfAction(TableAction.MERGE), untilDestroyed(this))
      .subscribe(rows => this.customerFacade.mergeDialog(rows.map(({ uuid }) => uuid)));
    // Edit
    actions$.pipe(rowOfAction(TableAction.EDIT), untilDestroyed(this)).subscribe(({ uuid }) => this.customerFacade.updateDialog(uuid));
    // Consult
    actions$
      .pipe(rowOfAction(TableAction.CONSULT), untilDestroyed(this))
      .subscribe(({ uuid, contactFirstName, contactLastName, contactCompanyName }) =>
        // TODO replace by Angular histo popin
        this.modalService.open({
          keyboard: false,
          backdrop: false,
          fullscreen: true,
          ctrlKeyboard: true,
          component: 'contactContactViewHistoPopin',
          modalId: 'histoModalContact_' + uuid,
          title: this.translateService.instant('agent:contact.info.popin.title') + ` ${contactLastName} ${contactFirstName}`,
          resolve: {
            contactCalling: () =>
              Option.of({
                ...initialBooking,
                customerId: uuid,
                customerName: this.buildCustomerName(contactFirstName, contactLastName, contactCompanyName),
              }),
            checkVisibilityMail: () => Option.of(false),
            inProcessActionsAllowed: () => Option.of(false),
          },
        })
      );
    // Delete
    actions$
      .pipe(rowOfAction(TableAction.DELETE), untilDestroyed(this))
      .subscribe(({ uuid }) => this.customerFacade.deleteConfirmDialog(uuid));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @angular-eslint/no-empty-lifecycle-method
  ngOnDestroy() {}
}
